
const listStyle = {
  position: 'absolute', top: '100%', insetInlineStart: 0, insetInlineEnd: 0, zIndex: 200,
  background: 'var(--c-surface)', border: '1px solid var(--c-border-strong)', borderRadius: 6,
  margin: 0, padding: 0, listStyle: 'none',
  maxHeight: 200, overflowY: 'auto',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
};

const rowStyle = {
  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
  borderBottom: '1px solid var(--c-bg-muted)',
};

const secondaryStyle = { fontSize: 11, color: 'var(--c-text-muted-fg)', marginTop: 2 };

/**
 * What an autocomplete box drops below itself.
 *
 * `box` is a hooks/useAutocomplete result, and this renders nothing unless it
 * is open with something to show, so no caller has to remember that pair of
 * conditions -- all four of them used to write it out by hand.
 *
 * Picking is on mouseDown rather than click, and that is not a detail: the
 * input's onBlur runs first on a click and closes this list, so by the time a
 * click would land there is nothing under the cursor to click. A suggestion
 * that cannot be clicked is worse than no suggestions at all, because the
 * person can see the answer they want and cannot take it.
 */
export default function SuggestionList({ box, testId, onPick, children }) {
  if (!box.open || box.suggestions.length === 0) return null;
  return (
    <ul style={listStyle}>
      {box.suggestions.map((item) => {
        const { key, body } = children(item);
        return (
          <li key={key}
            data-testid={testId}
            onMouseDown={() => onPick(item)}
            style={rowStyle}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--c-primary-soft)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--c-surface)'}
          >
            {body}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A region: the Hebrew name, which is what people search by, and the English
 * one underneath when there is one. Each is marked with its own direction so a
 * Hebrew name and a Latin one do not fight over the line.
 */
export function regionRow(loc) {
  return {
    key: loc.location_id,
    body: (
      <>
        <div dir="rtl">{loc.name_he}</div>
        {loc.name_en && <div dir="ltr" style={secondaryStyle}>{loc.name_en}</div>}
      </>
    ),
  };
}

/**
 * A plant: the binomial in italics, as a scientific name is written, with the
 * English common name beside it and the Hebrew one below.
 */
export function speciesRow(sp) {
  return {
    key: sp.species_id,
    body: (
      <>
        <div dir="ltr" style={{ fontStyle: 'italic' }}>
          {sp.scientific_name}{sp.common_name_en ? ` (${sp.common_name_en})` : ''}
        </div>
        {sp.hebrew_name && <div dir="rtl" style={secondaryStyle}>{sp.hebrew_name}</div>}
      </>
    ),
  };
}
